<template>
	<el-row>
		<el-col :span="24">
			<div class="base-form header">
				<!-- Creating mode -->
				<div v-if="isCreatingMode" style="display: flex">
					<div>
						<h2 style="margin: 0;">
							Новый заказ-наряд
						</h2>
					</div>
					<div v-if="ruleForm.applyManualOrderNumber">
						<text-editable-component
							v-model="ruleForm.orderNumber"
							class="highlighted-placeholder colored-font"
						/>
					</div>
					<div v-html="renderTotalCost()"></div>
				</div>
				<!-- Editing mode -->
				<div v-if="!isCreatingMode" style="display: flex">
					<div>
						<h2 style="margin: 0;">
							Заказ-наряд
						</h2>
					</div>
					<text-editable-component		
						v-if="ruleForm.applyManualOrderNumber"
						v-model="ruleForm.orderNumber"
						class="highlighted-placeholder colored-font"
					/>
					<span v-if="!ruleForm.applyManualOrderNumber && ruleForm.orderNumber?.length" class="highlighted-placeholder">
						{{ ruleForm.orderNumber }}
					</span>
					<div v-html="renderTotalCost()"></div>
					<order-print-form-component :order-id="ruleForm.id" icon-only />
				</div>
			</div>
		</el-col>

		<el-col :span="24" v-loading="isAsyncOperationPending">			
			<el-tabs class="order-tabs" v-model="orderActiveTab">
				<el-tab-pane label="Основное" name="common">
					<div class="base-form form" style="margin-left: 0;">						
						
						<order-common-form-component	
							ref="commonFormComponent"
							v-model="ruleForm.common"
							:order="existingOrder"
							:executives="executiveEntities"
							:creating-mode="isCreatingMode"
						/>
						
						<el-form ref="form" :rules="rules" :model="ruleForm">
							<el-form-item label-position="top" prop="orderNumber">
								<el-switch
									v-model="ruleForm.applyManualOrderNumber"
									@change="onApplyManualOrderNumberChange"
									:active-text="isCreatingMode ? 'Ручной ввод номера ЗН' : 'Разрешить изменение номера ЗН'"
								/>
							</el-form-item>
						</el-form>
					</div>							
				</el-tab-pane>
				<el-tab-pane :label="`Работы ${ruleForm?.jobs?.length ? `(${ruleForm.jobs.length})` : ''}`" name="jobs">
					<div>
						<order-jobs-component ref="jobsForm" 
							v-model:jobs="ruleForm.jobs"
							:order="orderMock"
						/>
					</div>
				</el-tab-pane>
				<el-tab-pane :label="`Запчасти ${ruleForm?.orderItems?.length ? `(${ruleForm.orderItems.length})` : ''}`" name="items">
					<div>
						<order-items-component ref="itemsForm" v-model:items="ruleForm.orderItems" v-model:itemDemands="ruleForm.orderItemDemands" />
					</div>
				</el-tab-pane>
				<el-tab-pane label="Рекомендации" name="recs">
					<h3>recomendations go here</h3>
				</el-tab-pane>
			</el-tabs>

			<el-form-item>
				<div class="base-form form">
					<button-loading-component v-if="isCreatingMode"
						label="Создать заказ-наряд" 
						@click="onCreateOrderClick"
					/>

					<button-loading-component v-else
						label="Сохранить изменения" 
						@click="onUpdateOrderClick"
					/>
					
					<el-button @click="resetForm" type="warning">
						Отменить изменения
					</el-button>
				</div>
			</el-form-item>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, Memo, Loading } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElForm, FormRules, FormInstance, ElIcon, ElNotification, ElMessageBox } from 'element-plus';
import { reactive, watch, WatchStopHandle } from 'vue';
import OrderJobsComponent from '@/components/order/OrderJobsComponent.vue';
import OrderItemsComponent from '@/components/order/OrderItemsComponent.vue';
import ButtonLoadingComponent from '@/components/ButtonLoadingComponent.vue';
import EventHelper from '@/helpers/eventsHelper';
import OrderModel from '@/models/OrderModel';
import LegalEntityModel from '@/models/LegalEntityModel';
import IOrderComponent from '@/components/order/IOrderComponent';
import EmployeeModel from '@/models/EmployeeModel';
import OrderJobViewModel from '@/ViewModels/OrderJobViewModel';
import OrderApiHelper from '@/helpers/api/OrderApiHelper';
import OrderItemViewModel from '@/ViewModels/OrderItemViewModel';
import OrderJobModel from '@/models/OrderJobModel';
import ServiceJobModel from '@/models/ServiceJobModel';
import OrderJobAssigneeModel from '@/models/OrderJobAssigneeModel';
import ServiceJobOperationModel from '@/models/ServiceJobOperationModel';
import OrderItemModel from '@/models/OrderItemModel';
import ItemModel from '@/models/ItemModel';
import JobsApiHelper from '@/helpers/api/JobsApiHelper';
import VehicleApiHelper from '@/helpers/api/VehicleApiHelper';
import OrderItemSelectionDemandModel from '@/models/OrderItemSelectionDemandModel';
import TextEditableComponent from '@/components/TextEditableComponent.vue';
import IOrderCommonForm from '@/components/order/IOrderCommonForm';
import OrderCommonFormComponent from '@/components/order/OrderCommonFormComponent.vue';
import store from '@/store';
import CounterpartyModel from '@/models/CounterpartyModel';
import Vehicle from '@/models/Vehicle';
import LegalEntityBranchModel from '@/models/LegalEntityBranchModel';
import OrderPrintFormComponent from '@/components/order/OrderPrintFormComponent.vue';

interface IOrderForm {
	id: number;
	orderNumber: string | null;	
		
	common: IOrderCommonForm;
	jobs: Array<OrderJobViewModel> | null;
	orderItems: Array<OrderItemViewModel> | null;
	orderItemDemands: Array<OrderItemSelectionDemandModel> | null;

	applyManualOrderNumber: boolean;
}

@Options({
	components: {
		CirclePlus, ElForm, ElTable, ElTag, ElLink, ElIcon, Memo, Loading, ElButton,
		OrderCommonFormComponent,
		OrderItemsComponent,
		OrderJobsComponent,
		ButtonLoadingComponent,
		TextEditableComponent,
		OrderPrintFormComponent
	},
	props: {
		params: Array
	}
})
export default class OrderAddView extends Vue {	
	/** props */
	params!: Array<any>;

	/** view-models */
	isCreatingMode = true;
	existingOrder: OrderModel | null = null;	
	orderApiHelper = new OrderApiHelper(store.getters.jwt);	
	vehicleApiHelper = new VehicleApiHelper();

	orderActiveTab = 'common';

	isAsyncOperationPending = false;

	executiveEntities: Array<LegalEntityModel> | null = null;

	orderMock = new OrderModel();
	ruleForm: IOrderForm = {
		id: 0,
		/** order's unique composite-number (can be generated on backend or by user)  */
		orderNumber: null,

		common: {
			id: 0,
			scheduledStartDate: new Date(),
			/** order initial status (id) */
			status: 0,
			/** order's responsible entity */
			executiveEntity: null,
			/** order's responsible entity branch */
			executiveEntityBranch: null,
			/** customer */
			customer: null,
			customerId: null,
			/** customer's vehicle */
			vehicle: null,	
			vehicleId: null,
			/** order's manager */
			managerId: null,

			customerNotes: null,
			internalNotes: null,
			notesForCustomer: null,
		},			
		jobs: null,
		orderItems: null,
		orderItemDemands: null,
		applyManualOrderNumber: false
	};

	rules = reactive<FormRules<any>>({		
		orderNumber: [
			{ validator: (a,b,c) => this.orderNumberValidator(a, b, c), trigger: 'blur' }
		]
	});

	ruleformWathSubscription: WatchStopHandle | null = null;

	mounted() {
		this.ruleformWathSubscription = watch(
			this.ruleForm,
			() => {
				this.orderMock.id = this.ruleForm.id;
				this.orderMock.number = this.ruleForm.orderNumber ?? '';
				this.orderMock.executiveEntityBranch = this.ruleForm.common.executiveEntityBranch;
				this.orderMock.scheduledStartDate = this.ruleForm.common.scheduledStartDate;
				this.orderMock.jobs = this.ruleForm.jobs;
				this.orderMock.vehicle = this.ruleForm.common.vehicle;
			}, 
			{ deep: true }
		);

		const requestedOrderId = this.params?.length ? (this.params[0] ? this.params[0] as number : undefined) : undefined;
		this.initView(requestedOrderId);
	}

	unmounted(): void {
		if (this.ruleformWathSubscription) {
			this.ruleformWathSubscription();
		}
	}

	/** initialize view with existing order's data or prepare fields for creating a new one */
	initView(orderId?: number) {
		this.executiveEntities = store.getters.executives;
		this.isCreatingMode = !orderId;

		if (this.isCreatingMode) {			
			this.ruleForm.common.managerId = store.getters.user.employee.id;
			this.ruleForm.common.scheduledStartDate = new Date();
			this.ruleForm.common.scheduledStartDate.setSeconds(0, 0);
			this.ruleForm.applyManualOrderNumber = true;

			if (this.executiveEntities?.length == 1) {
				this.ruleForm.common.executiveEntity = this.executiveEntities[0];
				if (this.ruleForm.common.executiveEntity?.branches?.length == 1) {
					this.ruleForm.common.executiveEntityBranch = this.ruleForm.common.executiveEntity.branches[0];
				}
			}
		}
		else {
			this.isAsyncOperationPending = true;
			this.getOrderAsync(orderId!)
				.then(async order => {
					this.existingOrder = order;

					this.ruleForm.id = order.id;
					this.ruleForm.applyManualOrderNumber = false;
					this.ruleForm.common.id = order.id;
					this.ruleForm.common.scheduledStartDate = order.scheduledStartDate;
					this.ruleForm.common.scheduledStartDate.setSeconds(0, 0);
					this.ruleForm.common.status = order.status;
					
					const existingOrderLegalEntityId = order.executiveEntityBranch!.legalEntity!.id;
					const existingOrderLegalEntityBranches = this.executiveEntities!.find(_ => _.id == existingOrderLegalEntityId)!.branches;
					this.existingOrder.executiveEntityBranch!.legalEntity!.branches = existingOrderLegalEntityBranches;

					this.ruleForm.common.executiveEntity = order.executiveEntityBranch!.legalEntity;
					this.ruleForm.common.executiveEntityBranch = order.executiveEntityBranch;
					this.ruleForm.common.managerId = order.manager!.id;

					this.ruleForm.common.customer = order.customer;
					this.ruleForm.common.vehicle = order.vehicle;

					this.ruleForm.common.customerNotes = order.customerNotes;
					this.ruleForm.common.internalNotes = order.internalNotes;
					this.ruleForm.common.notesForCustomer = order.externalNotes;

					if (order.jobs?.length) {						
						const mapOrderJobPromises = order.jobs.map(async (job) => {
							const orderJobViewModel = new OrderJobViewModel(job);
							orderJobViewModel.cost = await this.getOrderJobCostAsync(orderJobViewModel);
							orderJobViewModel.availableOperations = await this.getComponentAvailableOperationsAsync(job);
							return orderJobViewModel;
						});

						const jobs = await Promise.all(mapOrderJobPromises);
						this.ruleForm.jobs = [...jobs];
					}

					if (order.items?.length) {	
						const mappedOrderItems = order.items.map((i, index) => {							
							const item = new OrderItemViewModel(i);
							item.vmId = index;
							item.isItemPriceValid = true;
							item.isItemValid = true;
							return item;
						});
						this.ruleForm.orderItems = mappedOrderItems
					}

					if (order.itemSelectionDemands?.length) {	
						this.ruleForm.orderItemDemands = order.itemSelectionDemands.map(_ => new OrderItemSelectionDemandModel(_));				
					}

					await this.$nextTick();
					this.ruleForm.orderNumber = order.number;
				})
				.catch((e) => {
					console.error(`Unable to load order #${this.existingOrder!.id}`, e);
					ElMessageBox.confirm(
						'Ошибка загрузки заказ-наряда',						
						{
							type: 'error',
							confirmButtonText: 'Закрыть',
							cancelButtonText: 'Заново загрузить заказ-наряд',
						}
					).then(() => {
						const currentTabId = store.getters.tab as number;		
						store.commit('removeTab', currentTabId);
					}).catch(
						() => this.initView(this.existingOrder!.id)
					);
				})
				.finally(() => this.isAsyncOperationPending = false);
		}
	}	

	onApplyManualOrderNumberChange(value: boolean) {
		if (value) {
			if (!this.isCreatingMode) {
				this.ruleForm.orderNumber = this.existingOrder!.number;
			}
		}
	}

	onCreateOrderClick() {
		const form = this.$refs.form as FormInstance;
		form.validate(async isOrderNumberValid => {			
			const isCommonFormValid = await (this.$refs.commonFormComponent as IOrderComponent)
				.validateAsync();

			if (!isOrderNumberValid || !isCommonFormValid) {
				this.orderActiveTab = 'common';
				return;
			}

			const isOrderItemsValid = await (this.$refs.itemsForm as IOrderComponent)
				.validateAsync();

			if (!isOrderItemsValid) {
				this.orderActiveTab = 'items';
				return;
			}

			const isOrderJobsValid = await (this.$refs.jobsForm as IOrderComponent)
				.validateAsync();

			if (!isOrderJobsValid) {
				this.orderActiveTab = 'jobs';
				return;
			}

			this.isAsyncOperationPending = true;
			const orderCreateRequest = this.mapOrderRequest();
			const createdOrderId = await this.orderApiHelper.createOrderAsync(orderCreateRequest);
			this.isAsyncOperationPending = false;

			if (createdOrderId) {
				ElNotification({
					message: 'Заказ-наряд успешно создан',
					type: 'success',
				});
				EventHelper.dispatchEvent('ordersListUpdate');
				this.initView(createdOrderId);
			} else {
				ElNotification({
					message: `Ошибка создания заказ-наряда`,
					type: 'error',
				});
			}
		});
	}

	onUpdateOrderClick() {				
		const form = this.$refs.form as FormInstance;
		form.validate(async (isOrderNumberValid) => {
			const isCommonFormValid = await (this.$refs.commonFormComponent as IOrderComponent)
				.validateAsync();

			if (!isOrderNumberValid || !isCommonFormValid) {
				this.orderActiveTab = 'common';
				return;
			}

			const isOrderItemsValid = await (this.$refs.itemsForm as IOrderComponent)
				.validateAsync();				

			if (!isOrderItemsValid) {
				this.orderActiveTab = 'items';
				return;
			}

			const isOrderJobsValid = await (this.$refs.jobsForm as IOrderComponent)
				.validateAsync();

			if (!isOrderJobsValid) {
				this.orderActiveTab = 'jobs';
				return;
			}
			
			this.isAsyncOperationPending = true;
			const orderUpdateRequest = this.mapOrderRequest();
			const isUpdated = await this.orderApiHelper.updateOrderAsync(orderUpdateRequest);
			this.isAsyncOperationPending = false;

			if (isUpdated) {
				ElNotification({
					message: `Заказ-наряд ${this.existingOrder!.number} успешно изменен`,
					type: 'success',
				});
				this.initView(this.existingOrder!.id);
				EventHelper.dispatchEvent('ordersListUpdate');				
			} else {
				ElNotification({
					message: 'Ошибка сохранения изменений заказ-наряда',
					type: 'error',
				});
			}			
		});
	}
	
	resetForm() {
		ElMessageBox.confirm(
				'Все несохраненные данные буду потеряны. Продолжить ?',
				{
					confirmButtonText: 'OK',
					cancelButtonText: 'Отмена',
					type: 'warning',
					confirmButtonClass: 'messagebox-danger-button'
				}
			)
			.then(() => {				
				if (this.isCreatingMode) {
					this.ruleForm.orderNumber = null;
					this.ruleForm.applyManualOrderNumber = false;

					this.ruleForm.common.executiveEntity = null;
					this.ruleForm.common.executiveEntityBranch = null;
					this.ruleForm.common.customer = null;
					this.ruleForm.common.customerId = null;
					this.ruleForm.common.vehicle = null;
					this.ruleForm.common.vehicleId = null;

					this.ruleForm.jobs = [];
					this.ruleForm.orderItems = [];
					this.ruleForm.orderItemDemands = [];					
					
					this.initView();
				} else {
					this.initView(this.existingOrder!.id);
				}
			});
	}

	orderNumberValidator(rule: any, value: any, callback: Function) {
		if (this.ruleForm.applyManualOrderNumber &&
			!this.ruleForm.orderNumber) {
			callback(new Error('Номер заказ-наряда не указан'));
			return false;
		}

		return true;
	}

	closeCurrentTab() {		
		store.commit('removeTab', store.getters.tab);
	}

	/** get all available operations for a component */
	async getComponentAvailableOperationsAsync(job: OrderJobModel) : Promise<Array<ServiceJobOperationModel>> {		
		const componentId = job.serviceJob!.component!.id;
		const jobsApiHelper = new JobsApiHelper();
		const jobs = await jobsApiHelper.getJobsByComponentAsync(store.getters.jwt, componentId);
		if (!jobs.length) {
			return [];
		}

		return jobs.map(_ => _.operation);
	}

	getOrderJobCostAsync(orderJob: OrderJobViewModel) : Promise<number> {		
		return this.orderApiHelper.getJobCostAsync(orderJob);
	}

	async getOrderAsync(orderId: number) : Promise<OrderModel> {
		const order = await this.orderApiHelper.getOrderAsync(store.getters.jwt, orderId);
		if (!order?.id) {
			throw new Error('Unexpected response / empty response');
		}

		return order;
	}

	renderTotalCost() {
		let jobsCost = 0;
		if (this.ruleForm.jobs?.length) {
			this.ruleForm.jobs?.forEach(_ => jobsCost += _.cost);
		}

		let itemsCost = 0;
		if (this.ruleForm.orderItems?.length) {
			this.ruleForm.orderItems?.forEach(_ => itemsCost += _.totalPrice);
		}

		const total = jobsCost + itemsCost;

		if (!total) {
			return '';
		}

		const el = `${Math.round(total)} руб`;		
		return `<span class="highlighted-placeholder">${el}</span>`;
	}

	mapOrderRequest() : OrderModel {
		const itemsMapper = (items: Array<OrderItemViewModel> | null) => {
			if (!items?.length) {
				return [];
			}

			return items.map(_ => new OrderItemModel({
				id: _.id,
				count: _.count,
				discountPercent: _.discountPercent,
				overridenPrice: _.overridenPrice,
				item: new ItemModel({ id: _.item!.id }),
				manager: new EmployeeModel({ id: _.manager!.id }),
				purchasePrice: _.purchasePrice
			}));
		};

		const jobsMapper = (jobs: Array<OrderJobViewModel> | null) => {
			if (!jobs?.length) {
				return [];
			}			

			return jobs.map(_ => {
				return new OrderJobModel({
					id: _.id,
					discountPercent: _.discountPercent,
					estimationHrs: _.estimationHrs,
					overriddenJobCostPerHour: _.overriddenJobCostPerHour,
					isSubcontract: _.isSubcontract,

					jobOrder: _.jobOrder,

					serviceJob: new ServiceJobModel({
						id: _.serviceJob!.id
					}),

					manager: new EmployeeModel({
						id: _.manager!.id
					}),

					assignees: _.assignees?.map(a => new OrderJobAssigneeModel({
						id: a.id,
						allocationPercent: a.allocationPercent,
						employee: a.employee ? new EmployeeModel({ id: a.employee!.id }) : null,
						subcontractor: a.subcontractor ? new CounterpartyModel({id: a.subcontractor!.id}) : null,
						assigneeOrder: a.assigneeOrder,
						scheduledDate: a.scheduledDate,
						scheduledStartTime: a.scheduledStartTime,
						scheduledEndTime: a.scheduledEndTime,
						scheduledEndDate: a.scheduledEndDate
					})),

					notes: _.notes
				});
			});
		};

		const itemDemandsMapper = (mapItemSelectionDemands: Array<OrderItemSelectionDemandModel> | null) => {
			if (!mapItemSelectionDemands?.length) {
				return [];
			}

			return mapItemSelectionDemands
				.map((_: OrderItemSelectionDemandModel) => new OrderItemSelectionDemandModel(_));
		};

		const orderRequest = new OrderModel();		
		orderRequest.id = this.existingOrder?.id ? this.existingOrder.id : 0;
		orderRequest.number = this.ruleForm.orderNumber ?? '';
		orderRequest.manager = new EmployeeModel({ id: this.ruleForm.common.managerId });
		orderRequest.customer = new CounterpartyModel({ id: this.ruleForm.common.customer!.id });
		orderRequest.status = this.ruleForm.common.status;
		orderRequest.executiveEntityBranch = new LegalEntityBranchModel({ id: this.ruleForm.common.executiveEntityBranch!.id });
		orderRequest.vehicle =  new Vehicle({ id: this.ruleForm.common.vehicle!.id });
		orderRequest.externalNotes = this.ruleForm.common.notesForCustomer;
		orderRequest.internalNotes = this.ruleForm.common.internalNotes;
		orderRequest.customerNotes = this.ruleForm.common.customerNotes;
		orderRequest.items = itemsMapper(this.ruleForm.orderItems);
		orderRequest.jobs = jobsMapper(this.ruleForm.jobs);
		orderRequest.itemSelectionDemands = itemDemandsMapper(this.ruleForm.orderItemDemands);		
		orderRequest.scheduledStartDate = this.ruleForm.common.scheduledStartDate;
		
		return orderRequest;
	}
}
</script>

<style>
.messagebox-danger-button {
	border-color: var(--el-color-error);
	background: var(--el-color-error-light-3);
}

	.messagebox-danger-button:hover {
		border-color: var(--el-color-error);
		background: var(--el-color-danger);
	}

.order-tabs {
	margin: 0 1rem 1rem;
	border-bottom: 1px solid var(--el-border-color-light)
}

	.order-tabs div.is-active {
		border-top: 0!important
	}

	.highlighted-placeholder {
		background: var(--el-color-primary-light-9);		
		border-radius: var(--el-border-radius-base);
		margin: 0 1rem;
		padding: 0 1rem;
		line-height: 2.8rem;
		min-width: 2.28rem; 
		font-size: 2.28rem;
	}

		.highlighted-placeholder.colored-font {			
			color: var(--el-color-primary);
		}
</style>